import React, { Component } from 'react'
import { connect } from 'react-redux'
import DestinationSelector from '../../components/DestinationSelector/DestinationSelector'
import PartySelector from '../../components/PartySelector/PartySelector'
import DateSelector from '../../components/DateSelector/DateSelector'
import PropTypes from 'prop-types'
import SearchHeader from './SearchHeader'
import FooterButton from '../../components/FooterButton/FooterButton'
import Container from '../../components/Container/Container'
import PartySelectorAlert from '../../components/PartySelectorAlert/PartySelectorAlert'
import objectToParams from '../../helpers/objectToParams'
import objectContainsFalse from '../../helpers/objectContainsFalse'
import { tagSearchPage } from '../../helpers/tag/dynamicPageview'
import { updateParty } from '../../actions/groupDetails'
import {
  validateGroupDetails,
  validateSearchDetails,
} from '../../actions/validation'
import {
  updateSearchDetails,
  toggleSingleFlight,
  toggleMultipleDestinations,
} from '../../actions/searchDetails'
import { requestAirportRoutes } from '../../actions/airportRoutes'
import moment, { stringifyDate } from '../../helpers/momentUtils'
import getStaticData from '../../helpers/getStaticData'
import getPaxSummary from '../../helpers/getPaxSummary'
import addFocusKey from '../../helpers/addFocusKey'
const minPassengers = getStaticData('gossettings').mingroupofferpassengers
class Search extends Component {
  state = {
    errors: {
      primary: {},
      secondary: {},
    },
    outboundAirports: [],
    inboundAirports: [],
    showInputFeedback: false,
    labels: this.props.content('Search Flights'),
  }

  componentDidMount() {
    window.scrollTo(0, 0)
    this.getAirportsList(this.props)
    const { groupDetails, updateParty } = this.props
    const party = groupDetails['party'].map(group =>
      group.key === 'adults'
        ? {
            ...group,
            value: minPassengers,
          }
        : group
    )
    const defaultParty = this.addPaxLabels(party)
    updateParty(defaultParty)
    tagSearchPage()
  }

  componentDidUpdate = prevProps => {
    const { primary: oldPrimary, secondary: oldSecondary } = prevProps
    const { primary: newPrimary, secondary: newSecondary } = this.props
    if (prevProps !== this.props) {
      this.getAirportsList(this.props)
      this.getSecondaryAirportsList(this.props)
    }
    if (oldPrimary !== newPrimary || oldSecondary !== newSecondary) {
      this.validateSearchDetails(this.props)
    }
  }

  setSelectedAirport = (selectedAirport, type, id) => {
    const { requestAirportRoutes } = this.props
    requestAirportRoutes(selectedAirport, type, id)
  }

  setOutboundDate = date => {
    const { updateSearchDetails, selectedInboundDate } = this.props
    updateSearchDetails({
      ...(date > moment(selectedInboundDate, 'DD MMM YYYY') && {
        selectedInboundDate: stringifyDate(date),
      }),
      selectedOutboundDate: stringifyDate(date),
    })
  }

  setInboundDate = date => {
    const { updateSearchDetails } = this.props
    updateSearchDetails({
      selectedInboundDate: date ? stringifyDate(date) : '',
    })
  }

  validateRoute = (selectedAirport, possibleRoutes) => {
    if (!possibleRoutes.list) return false
    return possibleRoutes.list.some(x => x.key === selectedAirport.key)
  }

  validateSearchDetails = searchDetails => {
    const { primary, secondary } = searchDetails
    const { validation, validateSearchDetails } = this.props
    const primaryRoute = `${primary.inboundAirport.key}${
      primary.outboundAirport.key
    }`
    const secondaryRoute = `${secondary.inboundAirport.key}${
      secondary.outboundAirport.key
    }`
    const validatedSearchDetails = {
      ...validation.searchDetails,
      primary: {
        inboundAirport: Boolean(primary.inboundAirport.key),
        outboundAirport: Boolean(primary.outboundAirport.key),
        inboundRoute: this.validateRoute(
          primary.inboundAirport,
          primary.filteredOutboundAirports
        ),
        outboundRoute: this.validateRoute(
          primary.outboundAirport,
          primary.filteredInboundAirports
        ),
      },
      secondary: {
        inboundAirport: Boolean(secondary.inboundAirport.key),
        outboundAirport: Boolean(secondary.outboundAirport.key),
        inboundRoute: this.validateRoute(
          secondary.inboundAirport,
          secondary.filteredOutboundAirports
        ),
        outboundRoute: this.validateRoute(
          secondary.outboundAirport,
          secondary.filteredInboundAirports
        ),
      },
      routeCombination: primaryRoute !== secondaryRoute,
    }
    this.setState({
      errors: this.setErrorMessages(this.state.labels, validatedSearchDetails),
    })
    validateSearchDetails({ ...validatedSearchDetails })
  }

  addPaxLabels = party => {
    const t = this.props.content('Search Flights')
    const label = (key, value) => {
      switch (key) {
        case 'adults':
          return value > 1 ? t['Adult Plural Label'] : t['Adult Single Label']
        case 'children':
          return value > 1 ? t['Child Plural Label'] : t['Child Single Label']
        default:
          return value > 1 ? t['Baby Plural Label'] : t['Baby Single Label']
      }
    }
    return party.map(x => ({
      ...x,
      label: label(x.key, x.value),
    }))
  }

  handlePartyUpdate = (key, value) => {
    const { updateParty } = this.props
    const { party } = this.props.groupDetails
    const selectedGroup = party.find(group => group.key === key)
    selectedGroup.value = value
    const updatedParty = this.addPaxLabels(party)
    updateParty(updatedParty)
    this.validateParty(updatedParty)
  }

  validateParty = party => {
    const { validateGroupDetails } = this.props
    const [adults, children, infants] = party
    const amountOfPassengers = adults.value + children.value
    validateGroupDetails({
      party: {
        minGroupSize: amountOfPassengers >= minPassengers,
        minAdult: adults.value > 0,
        minAdultPerBaby: adults.value >= infants.value,
      },
    })
  }

  setErrorMessages = (labels, valid) => {
    const {
      secondary,
      primary,
      isMultipleDestination,
      lastSelectedAirport,
    } = this.props
    const errorMessages = {
      inboundRoute: labels['Invalid route destination error'],
      outboundRoute: labels['Invalid route origin error'],
      outboundAirport: labels['Invalid departure airport error'],
      inboundAirport: labels['Invalid arrival airport error'],
      duplicateRoute: labels['Duplicate Route Selected'],
    }
    const errors = {
      primary: objectContainsFalse(valid.primary)
        ? {
            ...this.getErrorMessage(
              primary,
              valid.primary,
              errorMessages,
              lastSelectedAirport.primary
            ),
          }
        : {},
      secondary:
        objectContainsFalse(valid.secondary) && isMultipleDestination
          ? {
              ...this.getErrorMessage(
                secondary,
                valid.secondary,
                errorMessages,
                lastSelectedAirport.secondary
              ),
            }
          : {},
    }
    if (isMultipleDestination && !valid.routeCombination) {
      errors.secondary.inbound = errorMessages.duplicateRoute
    }
    return errors
  }

  formatOptions = (list, index = 'default') => {
    return list.map(option => addFocusKey(option, index))
  }

  getErrorMessage = (input, valid, labels, lastSelectedAirport) => {
    let outbound = !valid.outboundAirport && labels.outboundAirport
    let inbound = !valid.inboundAirport && labels.inboundAirport

    if (valid.outboundAirport && valid.inboundAirport) {
      if (!valid.outboundRoute && lastSelectedAirport === 'outbound') {
        outbound = labels.outboundRoute
          .replace(/{destination}/g, input.inboundAirport.label)
          .replace(/{origin}/g, input.outboundAirport.label)
      }
      if (!valid.inboundRoute && lastSelectedAirport === 'inbound') {
        inbound = labels.inboundRoute
          .replace(/{origin}/g, input.outboundAirport.label)
          .replace(/{destination}/g, input.inboundAirport.label)
      }
    }
    return { outbound, inbound }
  }

  getAirportsList(props) {
    const { primary, airports } = props
    const inboundDefaultAirports = {
      title: 'Origin to secondary airports',
      list: this.formatOptions(airports),
    }
    const outboundDefaultAirports = {
      title: 'Destinations from other airports',
      list: this.formatOptions(airports),
    }

    if (primary.filteredInboundAirports.list) {
      const inboundAirports = [
        {
          ...primary.filteredInboundAirports,
          list: this.formatOptions(
            primary.filteredInboundAirports.list,
            'filtered'
          ),
        },
        inboundDefaultAirports,
      ]
      this.setState({
        inboundAirports,
      })
    } else {
      this.setState({
        inboundAirports: this.formatOptions(airports),
      })
    }

    if (primary.filteredOutboundAirports.list) {
      const outboundAirports = [
        {
          ...primary.filteredOutboundAirports,
          list: this.formatOptions(
            primary.filteredOutboundAirports.list,
            'filtered'
          ),
        },
        outboundDefaultAirports,
      ]
      this.setState({
        outboundAirports,
      })
    } else {
      this.setState({
        outboundAirports: this.formatOptions(airports),
      })
    }
  }

  getSecondaryAirportsList(props) {
    const { secondary, airports } = props
    const inboundDefaultAirports = {
      title: 'Origin to other airports',
      list: this.formatOptions(airports),
    }
    const outboundDefaultAirports = {
      title: 'Destinations from other airports',
      list: this.formatOptions(airports),
    }

    if (secondary.filteredInboundAirports.list) {
      const inboundSecondaryAirports = [
        {
          ...secondary.filteredInboundAirports,
          list: this.formatOptions(
            secondary.filteredInboundAirports.list,
            'filtered'
          ),
        },
        inboundDefaultAirports,
      ]
      this.setState({
        inboundSecondaryAirports,
      })
    } else {
      this.setState({
        inboundSecondaryAirports: this.formatOptions(airports),
      })
    }

    if (secondary.filteredOutboundAirports.list) {
      const outboundSecondaryAirports = [
        {
          ...secondary.filteredOutboundAirports,
          list: this.formatOptions(
            secondary.filteredOutboundAirports.list,
            'filtered'
          ),
        },
        outboundDefaultAirports,
      ]
      this.setState({
        outboundSecondaryAirports,
      })
    } else {
      this.setState({
        outboundSecondaryAirports: this.formatOptions(airports),
      })
    }
  }

  getQueryStringParams() {
    let queryStringParams = {}
    const {
      selectedOutboundDate,
      selectedInboundDate,
      primary,
      groupDetails,
      isSingleFlight,
    } = this.props
    const [adults, children, infants] = groupDetails.party
    const data = {
      od: moment(selectedOutboundDate).format('D'),
      om: moment(selectedOutboundDate).format('M'),
      oy: moment(selectedOutboundDate).format('YYYY'),
      id: moment(selectedInboundDate).format('D'),
      im: moment(selectedInboundDate).format('M'),
      iy: moment(selectedInboundDate).format('YYYY'),
      ds: primary.outboundAirport.key,
      as: primary.inboundAirport.key,
      ap: adults.value,
      cp: children.value,
      ip: infants.value,
      r: !isSingleFlight,
    }
    Object.keys(data).forEach(x => {
      if (data[x] && data[x] !== 'Invalid date') {
        queryStringParams[x] = data[x]
      }
    })
    return queryStringParams
  }

  renderPartyError = () => {
    const { validation } = this.props
    const { labels } = this.state
    return (
      <PartySelectorAlert
        validation={validation.party}
        handleClick={() => this.contextRedirect()}
        labels={{
          minGroupSizeMessage: labels['Group too small message'],
          minAdultMessage: labels['Not enough adults message'],
          minAdultPerBabyMessage: labels['Adult baby balance message'],
          bookAFlightButtonLabel: labels['Book a flight button label'],
        }}
      />
    )
  }

  contextRedirect = () => {
    const redirectUrl = getStaticData('gossettings').bookingflowflightsearchurl
    const params = objectToParams(this.getQueryStringParams())
    window.open(`${redirectUrl}?${params}`, '_blank')
  }

  isAllInputValid = () => {
    const { isMultipleDestination, validation } = this.props
    const { party, searchDetails } = validation
    const validatedInput = !isMultipleDestination
      ? {
          ...searchDetails.primary,
          minAdult: party.minAdult,
          minGroupSize: party.minGroupSize,
        }
      : {
          ...searchDetails.primary,
          ...searchDetails.secondary,
          minAdult: party.minAdult,
          minGroupSize: party.minGroupSize,
          routeCombination: searchDetails.routeCombination,
        }
    return !objectContainsFalse(validatedInput)
  }

  goToNextStep = () => {
    const { history, validation } = this.props
    if (this.isAllInputValid()) {
      history.push('/flight-offers')
    } else {
      this.setState({
        showInputFeedback: true,
        errors: this.setErrorMessages(
          this.state.labels,
          validation.searchDetails
        ),
      })
    }
  }

  render() {
    const {
      inboundAirports,
      outboundAirports,
      labels,
      showInputFeedback,
      inboundSecondaryAirports,
      outboundSecondaryAirports,
      errors,
    } = this.state
    const {
      primary,
      secondary,
      selectedOutboundDate,
      selectedInboundDate,
      isSingleFlight,
      isMultipleDestination,
      toggleSingleFlight,
      groupDetails,
      toggleMultipleDestinations,
      validation,
    } = this.props
    return (
      <Container paddedBottom>
        <SearchHeader content={labels} />
        <DestinationSelector
          id="primary"
          key="primary"
          setSelectedAirport={this.setSelectedAirport}
          outboundAirports={inboundAirports}
          inboundAirports={outboundAirports}
          selectedInboundAirport={primary.inboundAirport}
          selectedOutboundAirport={primary.outboundAirport}
          isMultipleDestination={isMultipleDestination}
          inboundErrorMessage={errors && errors.primary.inbound}
          outboundErrorMessage={errors && errors.primary.outbound}
          showInputFeedback={showInputFeedback}
          labels={{
            fromLabel: labels['From'],
            toLabel: labels['To'],
            fromPlaceholder: labels['Origin Placeholder'],
            toPlaceholder: labels['Destination Placeholder'],
          }}
        />
        {isMultipleDestination && (
          <DestinationSelector
            id="secondary"
            key="secondary"
            setSelectedAirport={this.setSelectedAirport}
            outboundAirports={inboundSecondaryAirports}
            inboundAirports={outboundSecondaryAirports}
            selectedInboundAirport={secondary.inboundAirport}
            selectedOutboundAirport={secondary.outboundAirport}
            isMultipleDestination={isMultipleDestination}
            inboundErrorMessage={errors && errors.secondary.inbound}
            outboundErrorMessage={errors && errors.secondary.outbound}
            showInputFeedback={showInputFeedback}
            labels={{
              fromLabel: labels['From'],
              toLabel: labels['To'],
              fromPlaceholder: labels['Origin Placeholder'],
              toPlaceholder: labels['Destination Placeholder'],
            }}
          />
        )}
        <DateSelector
          setOutboundDate={this.setOutboundDate}
          setInboundDate={this.setInboundDate}
          selectedOutboundDate={selectedOutboundDate}
          selectedInboundDate={selectedInboundDate}
          toggleSingleFlightState={toggleSingleFlight}
          isSingleFlight={isSingleFlight}
          isMultipleDestination={isMultipleDestination}
          labels={{
            singleFlightLabel: labels['Single flight label'],
            inboundLabel: labels['Outbound'],
            outboundLabel: labels['Inbound'],
          }}
        />
        <PartySelector
          validation={validation.party}
          minPassengers={minPassengers}
          groupDetails={groupDetails}
          summary={getPaxSummary(groupDetails.party)}
          handleChange={this.handlePartyUpdate}
          contextRedirect={this.contextRedirect}
          labels={{
            passengersLabel: labels['Passengers'],
            yearsLabel: labels['Years Label'],
            groupDescription: labels['Group Description'],
          }}
        >
          {this.renderPartyError()}
        </PartySelector>
        {objectContainsFalse(validation.party) && this.renderPartyError()}
        <span
          className="link"
          role="button"
          onClick={() => {
            return toggleMultipleDestinations()
          }}
        >
          {!isMultipleDestination
            ? labels['Multiple Destinations Button Label']
            : labels['Single Destination Button Label']}
        </span>
        <FooterButton
          buttonAction={
            !validation.party.minGroupSize
              ? () => this.contextRedirect()
              : () => this.goToNextStep()
          }
          buttonContent={labels['Select Flights Button Label']}
          iconName="arrow-right"
        />
      </Container>
    )
  }
}

Search.propTypes = {
  airports: PropTypes.array,
  groupDetails: PropTypes.shape({
    groupName: PropTypes.string,
    party: PropTypes.array,
  }),
  selectedOutboundDate: PropTypes.string,
  selectedInboundDate: PropTypes.string,
  primary: PropTypes.object,
  secondary: PropTypes.object,
  isSingleFlight: PropTypes.bool,
  toggleSingleFlight: PropTypes.func,
  resetSearch: PropTypes.func,
  partyIsValid: PropTypes.bool,
  content: PropTypes.func,
  toggleMultipleDestinations: PropTypes.func,
  history: PropTypes.object,
  isMultipleDestination: PropTypes.bool,
  lastSelectedAirport: PropTypes.object,
  validation: PropTypes.object,
  updateParty: PropTypes.func,
  validateGroupDetails: PropTypes.func,
  validateSearchDetails: PropTypes.func,
  updateSearchDetails: PropTypes.func,
  requestAirportRoutes: PropTypes.func,
}

const mapStateToProps = state => {
  return {
    groupDetails: state.groupDetails,
    ...state.searchDetails,
    validation: {
      party: state.validation.groupDetails.party,
      searchDetails: state.validation.searchDetails,
    },
  }
}

const mapDispatchToProps = {
  updateParty,
  validateGroupDetails,
  validateSearchDetails,
  updateSearchDetails,
  toggleSingleFlight,
  toggleMultipleDestinations,
  requestAirportRoutes,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Search)
